/*
 * Knuska hero banner - slider behaviour.
 *
 * Liquid marks the first slide active and every other one aria-hidden, so the
 * banner is correct before this runs and stays correct if it never does. What
 * is added here is movement: a timer, the dots, the keyboard, and a swipe.
 *
 * The countdown in the dots is driven by requestAnimationFrame against a real
 * clock rather than by a CSS animation on a fixed duration. A CSS animation
 * cannot be paused and resumed at an arbitrary point without being restarted,
 * which would show the ring snapping back every time a pointer left the banner.
 */
if (!customElements.get('knuska-hero-banner')) {
  const REDUCED = '(prefers-reduced-motion: reduce)';

  class KnuskaHeroBanner extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-slider')) return;

      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-hb-dot]'));
      this.status = this.querySelector('[data-hb-status]');
      if (this.slides.length < 2) return;

      this.index = 0;
      this.every = (Number(this.dataset.interval) || 6) * 1000;

      /*
       * Autoplay is a request, not a promise: a visitor who has asked for less
       * motion gets the slider without the timer, and moves it themselves.
       */
      this.wants =
        this.dataset.autoplay === 'true' &&
        !(window.matchMedia && window.matchMedia(REDUCED).matches);

      this.show(0);
      this.bind();
      if (this.wants) this.play();
    }

    disconnectedCallback() {
      this.pause();
      if (this.seen) this.seen.disconnect();
      if (this.onVisibility) {
        document.removeEventListener('visibilitychange', this.onVisibility);
      }
    }

    bind() {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.show(Number(dot.getAttribute('data-hb-dot')));
          this.restart();
        });
      });

      this.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        this.show(this.index + (e.key === 'ArrowRight' ? 1 : -1));
        this.restart();
      });

      ['pointerenter', 'focusin'].forEach((t) =>
        this.addEventListener(t, () => this.pause())
      );
      ['pointerleave', 'focusout'].forEach((t) =>
        this.addEventListener(t, () => {
          if (this.wants) this.play();
        })
      );

      this.swipe();

      /* A banner off-screen should not be spending frames or turning slides. */
      if ('IntersectionObserver' in window) {
        this.seen = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && this.wants) this.play();
            else this.pause();
          });
        });
        this.seen.observe(this);
      }

      document.addEventListener(
        'visibilitychange',
        (this.onVisibility = () => {
          if (document.hidden) this.pause();
          else if (this.wants) this.play();
        })
      );
    }

    /* Horizontal drags move the slider; vertical ones must still scroll. */
    swipe() {
      let x = 0;
      let y = 0;
      let tracking = false;

      this.addEventListener(
        'touchstart',
        (e) => {
          x = e.changedTouches[0].clientX;
          y = e.changedTouches[0].clientY;
          tracking = true;
        },
        { passive: true }
      );

      this.addEventListener(
        'touchend',
        (e) => {
          if (!tracking) return;
          tracking = false;
          const dx = e.changedTouches[0].clientX - x;
          const dy = e.changedTouches[0].clientY - y;
          if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
          this.show(this.index + (dx < 0 ? 1 : -1));
          this.restart();
        },
        { passive: true }
      );
    }

    show(next) {
      const count = this.slides.length;
      const to = ((next % count) + count) % count;

      this.slides.forEach((slide, i) => {
        const on = i === to;
        slide.classList.toggle('is-active', on);
        /*
         * A hidden slide is not just invisible - its links must leave the tab
         * order too, or the keyboard walks into a slide nobody can see.
         */
        /* aria-hidden takes "true", not an empty attribute. */
        if (on) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
        slide.querySelectorAll('a, button').forEach((el) => {
          if (on) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      });

      this.dots.forEach((dot, i) => {
        const on = i === to;
        dot.classList.toggle('is-active', on);
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
        dot.style.removeProperty('--hb-progress');
      });

      this.index = to;
      if (this.status) this.status.textContent = to + 1 + ' / ' + count;
    }

    /* Timing -------------------------------------------------------------- */

    play() {
      if (this.frame) return;
      this.startedAt = performance.now() - (this.elapsed || 0);
      this.frame = requestAnimationFrame(this.tick.bind(this));
    }

    pause() {
      if (!this.frame) return;
      cancelAnimationFrame(this.frame);
      this.frame = null;
      /* Keep the position, so resuming continues rather than starting over. */
      this.elapsed = performance.now() - this.startedAt;
    }

    restart() {
      this.elapsed = 0;
      if (!this.frame) return;
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.play();
    }

    tick(now) {
      const done = Math.min((now - this.startedAt) / this.every, 1);

      const dot = this.dots[this.index];
      if (dot) dot.style.setProperty('--hb-progress', done);

      if (done >= 1) {
        this.elapsed = 0;
        this.show(this.index + 1);
        this.startedAt = now;
      }

      this.frame = requestAnimationFrame(this.tick.bind(this));
    }
  }

  customElements.define('knuska-hero-banner', KnuskaHeroBanner);
}
